// Longevity report → A4 landscape PDF (two pages: Overview + Detailed Results).
// Drawn with jsPDF vector primitives so it prints crisply at any zoom and
// carries no Emergent branding (SYSTEM_RULES §4).
import jsPDF from "jspdf";

const SAGE = [46, 139, 99];
const INK = [44, 45, 40];
const MUTED = [120, 118, 110];
const TRACK = [231, 228, 221];
const CARD = [252, 251, 248];
const BORDER = [222, 218, 209];

let _logoPromise = null;
function loadLogo() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = "/brand/cocoon-logo.png";
  });
  return _logoPromise;
}

const hexToRgb = (hex) => {
  const h = String(hex || "#2e8b63").replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

function card(doc, x, y, w, h, r = 3) {
  doc.setFillColor(...CARD);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, r, r, "FD");
}

function overline(doc, text, x, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(String(text).toUpperCase(), x, y);
}

// Donut drawn as a sequence of short arcs (jsPDF has no arc primitive).
function donut(doc, cx, cy, radius, thickness, pct, color) {
  const steps = 150;
  const filled = Math.round(steps * Math.max(0, Math.min(1, pct)));
  doc.setLineWidth(thickness);
  doc.setLineCap("butt");
  const seg = (from, to, rgb) => {
    doc.setDrawColor(...rgb);
    for (let i = from; i < to; i += 1) {
      const a1 = (-90 + (360 / steps) * i) * (Math.PI / 180);
      const a2 = (-90 + (360 / steps) * (i + 1)) * (Math.PI / 180);
      doc.line(cx + radius * Math.cos(a1), cy + radius * Math.sin(a1),
               cx + radius * Math.cos(a2), cy + radius * Math.sin(a2));
    }
  };
  seg(0, steps, TRACK);
  if (filled > 0) seg(0, filled, color);
}

function pageHeader(doc, logo, report, title, subtitle, pageW) {
  if (logo) {
    const h = 13;
    const w = (logo.w / logo.h) * h;
    doc.addImage(logo.dataUrl, "PNG", 12, 8, w, h);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...INK);
    doc.text("COCOON", 12, 16);
  }
  doc.setDrawColor(...BORDER);
  doc.line(62, 8, 62, 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...INK);
  doc.text(title, 68, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(subtitle, 68, 21);

  // Guest info box (top right) — on both pages.
  const bw = 62;
  const bx = pageW - bw - 12;
  card(doc, bx, 7, bw, 16, 2);
  doc.setFontSize(7.5);
  const rows = [
    ["Name", report.guest?.name || "—"],
    ["Age", report.guest?.age != null ? String(report.guest.age) : "—"],
    ["Date", new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })],
  ];
  rows.forEach(([k, v], i) => {
    const ry = 11.5 + i * 4.3;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(k, bx + 4, ry);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(String(v).slice(0, 26), bx + 20, ry);
  });
}

export async function downloadLongevityReport(report) {
  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();   // 297
  const areas = report.areas || [];

  // ------------------------------------------------------------------
  // Page 1 — Your Longevity Overview
  // ------------------------------------------------------------------
  pageHeader(doc, logo, report, "YOUR LONGEVITY OVERVIEW", "A healthier, younger you", pageW);

  // Longevity score
  card(doc, 12, 28, 86, 62);
  overline(doc, "Cocoon Longevity Score", 17, 35);
  const ov = report.overall?.score;
  donut(doc, 55, 58, 15, 5, ov == null ? 0 : ov / 100, SAGE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(ov == null ? "—" : String(ov), 55, 59, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text("/ 100", 55, 64, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...SAGE);
  doc.text(String(report.overall?.band || "—").toUpperCase(), 55, 78, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(doc.splitTextToSize(report.overall?.sentence || "", 76), 55, 83, { align: "center" });

  // Age profile
  card(doc, 102, 28, 183, 62);
  overline(doc, "Your Age Profile", 107, 35);
  const ages = [
    [report.ages?.chronological, "Chronological Age", "Your actual age"],
    [report.ages?.functional, "Cocoon Biometric 360", report.ages?.functional_label],
    [report.ages?.brain, "Brain Age", report.ages?.brain_label],
  ];
  ages.forEach(([val, caption, note], i) => {
    const cx = 148 + i * 60;
    doc.setDrawColor(...BORDER);
    doc.setFillColor(248, 246, 241);
    doc.circle(cx, 53, 13, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...INK);
    doc.text(val == null ? "—" : String(val), cx, 54, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(...MUTED);
    doc.text("YEARS", cx, 58.5, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(caption, 52), cx, 71, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(note || "", 52), cx, 76, { align: "center" });
  });

  // 7 diagnostic areas
  card(doc, 12, 94, 273, 42);
  overline(doc, "Your 7 Diagnostic Areas", 17, 101);
  areas.forEach((a, i) => {
    const cx = 32 + i * 38.6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(a.name, 34), cx, 110, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...INK);
    doc.text(a.score == null ? "—" : String(a.score), cx, 124, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(...MUTED);
    doc.text(`/ ${a.max}`, cx, 128, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(...SAGE);
    doc.text(String(a.band || "").toUpperCase(), cx, 133, { align: "center" });
  });

  // Progress over time
  card(doc, 12, 140, 180, 58);
  overline(doc, "Your Progress Over Time (Overall Score)", 17, 147);
  const pts = (report.sessions || []).filter((s) => s.overall != null);
  const gx = 24; const gy = 188; const gw = 158; const gh = 32;
  doc.setDrawColor(...TRACK);
  doc.setLineWidth(0.2);
  [0, 25, 50, 75, 100].forEach((v) => {
    const y = gy - (v / 100) * gh;
    doc.line(gx, y, gx + gw, y);
    doc.setFontSize(5.5);
    doc.setTextColor(...MUTED);
    doc.text(String(v), gx - 3, y + 1, { align: "right" });
  });
  if (pts.length) {
    doc.setDrawColor(...SAGE);
    doc.setFillColor(...SAGE);
    doc.setLineWidth(0.6);
    const step = pts.length > 1 ? gw / (pts.length - 1) : 0;
    const xy = pts.map((p, i) => [gx + (pts.length > 1 ? i * step : gw / 2), gy - (p.overall / 100) * gh]);
    xy.forEach(([x, y], i) => {
      if (i > 0) doc.line(xy[i - 1][0], xy[i - 1][1], x, y);
    });
    xy.forEach(([x, y], i) => {
      doc.circle(x, y, 0.9, "F");
      doc.setFontSize(6);
      doc.setTextColor(...INK);
      doc.text(String(pts[i].overall), x, y - 2.5, { align: "center" });
      doc.setFontSize(5.5);
      doc.setTextColor(...MUTED);
      doc.text(pts[i].date || "", x, gy + 4, { align: "center" });
    });
  } else {
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text("No sessions recorded yet.", gx + gw / 2, gy - gh / 2, { align: "center" });
  }

  // Key improvements + next focus
  card(doc, 196, 140, 89, 25);
  overline(doc, "Key Improvements", 201, 146);
  (report.improvements || []).slice(0, 3).forEach((im, i) => {
    const y = 152 + i * 4.4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...(im.improved ? SAGE : [160, 60, 60]));
    doc.text(im.display, 201, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(im.label, 222, y);
  });
  if (!(report.improvements || []).length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text("Appears after your second set of measurements.", 201, 154);
  }

  card(doc, 196, 168, 89, 30);
  overline(doc, "Your Next Focus", 201, 174);
  (report.next_focus || []).slice(0, 3).forEach((f, i) => {
    const y = 179 + i * 6.4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(...INK);
    doc.text(`${i + 1}. ${f.area}`, 201, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(f.advice || "", 78).slice(0, 1), 201, y + 2.6);
  });

  // ------------------------------------------------------------------
  // Page 2 — Your Detailed Results
  // ------------------------------------------------------------------
  doc.addPage("a4", "landscape");
  pageHeader(doc, logo, report, "YOUR DETAILED RESULTS",
    "7 diagnostic areas for a longer, healthier, brighter you", pageW);

  const colW = 134;
  const rowH = 42;
  areas.forEach((a, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = 12 + col * (colW + 7);
    const y = 28 + row * (rowH + 4);
    card(doc, x, y, colW, rowH);

    const tint = hexToRgb(a.tint_hex || "#2e8b63");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...INK);
    doc.text(`${i + 1}.  ${a.name.toUpperCase()}`, x + 5, y + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.3);
    doc.setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(a.subtitle || "", colW - 12), x + 5, y + 11.5);

    donut(doc, x + 24, y + 28, 9, 3.4, a.score == null ? 0 : a.score / a.max, tint);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    doc.text(a.score == null ? "—" : String(a.score), x + 24, y + 28.5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    doc.setTextColor(...MUTED);
    doc.text(`/ ${a.max}`, x + 24, y + 32, { align: "center" });

    // last 3 session bars
    const hist = (a.history || []).slice(-3);
    hist.forEach((h, hi) => {
      const bx = x + 46 + hi * 11;
      const bh = Math.max(2, ((h.score ?? 0) / a.max) * 16);
      doc.setFillColor(...tint);
      doc.rect(bx, y + 32 - bh, 6, bh, "F");
      doc.setFontSize(5.5);
      doc.setTextColor(...INK);
      doc.text(String(h.score), bx + 3, y + 30 - bh, { align: "center" });
      doc.setTextColor(...MUTED);
      doc.text(String(h.date || "").slice(5), bx + 3, y + 35, { align: "center" });
    });
    if (!hist.length) {
      doc.setFontSize(6);
      doc.setTextColor(...MUTED);
      doc.text("No sessions yet", x + 52, y + 28);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...tint);
    doc.text(String(a.band || "").toUpperCase(), x + colW - 6, y + 28, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(...MUTED);
    doc.text("View detailed results in your portal", x + colW - 6, y + 34, { align: "right" });
  });

  const safe = String(report.guest?.name || "guest").replace(/[^\w]+/g, "_").toLowerCase();
  doc.save(`cocoon_longevity_report_${safe}.pdf`);
}

export default downloadLongevityReport;
